/**
 * Global environment selector (W1b) — the header control that drives
 * EnvironmentContext. A compact segmented switch; unprovisioned environments
 * (UAT/PROD today) render disabled with a "not provisioned" tooltip (RT-16).
 */
import React from 'react';
import { useEnvironment, type Environment } from '../contexts/EnvironmentContext';

const LABEL: Record<Environment, string> = { dev: 'DEV', test: 'TEST', uat: 'UAT', prod: 'PROD' };

const EnvironmentSelector: React.FC = () => {
  const { environment, setEnvironment, environments, provisioned } = useEnvironment();
  return (
    <div
      role="group"
      aria-label="Environment"
      className="hidden sm:flex items-center rounded-lg border border-iflow-900/10 bg-slate-50 overflow-hidden"
    >
      {environments.map((env, i) => {
        const isActive = env === environment;
        const isProvisioned = provisioned.includes(env);
        return (
          <button
            key={env}
            type="button"
            aria-pressed={isActive}
            disabled={!isProvisioned}
            title={isProvisioned ? `View ${LABEL[env]}` : `${LABEL[env]} — not provisioned`}
            onClick={() => isProvisioned && setEnvironment(env)}
            className={[
              'px-3 py-1.5 text-xs font-semibold transition-colors',
              i > 0 ? 'border-l border-iflow-900/10' : '',
              isActive
                ? 'bg-iflow-900 text-white'
                : isProvisioned
                  ? 'text-slate-500 hover:text-iflow-800 hover:bg-white'
                  : 'text-slate-300 cursor-not-allowed',
            ].join(' ')}
          >
            {LABEL[env]}
          </button>
        );
      })}
    </div>
  );
};

export default EnvironmentSelector;
